import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { KPI } from "../../../components/primitives.jsx";
import { useToast } from "../../../components/Toast";
import { useChartPalette } from "../../../theme/useChartPalette";
import { localizedName } from "../../../lib/localizedName";
import type { ApiError } from "../../../api/client";
import { labArchiveApi, type ArchiveInsights } from "../../../api/labArchive";

/**
 * Ten years of lab measurements looked at at once.
 *
 * Each section carries one finding that no single analysis shows, and every chart stands beside a
 * sentence saying what it shows. A chart without that sentence is decoration.
 *
 * Two things are deliberately NOT shown. There is no compliance percentage, because the inherited
 * limits cannot carry one: they hang off the article and the customer, not off a product
 * specification. The cards under that section count limit faults the way the archive reads limits,
 * and any of them can be 0, so the note beside them names no count and no worked example.
 * Nor is there a ranking of people by result, because a difference in what was measured comes from
 * WHAT a shift measures, not from who measures it.
 */
export default function LabInsightsTab() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";
  const palette = useChartPalette();

  const [data, setData] = useState<ArchiveInsights | null>(null);

  useEffect(() => {
    let ziv = true;
    void labArchiveApi
      .insights()
      .then((sveze) => {
        if (ziv) setData(sveze);
      })
      .catch((e: ApiError) => {
        if (ziv) toast.error(e.message);
      });
    return () => {
      ziv = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const n = (v: number) => v.toLocaleString(lang);

  // Every hook stands above every return from this function. The first pass has no data yet and must
  // count the same hooks as the pass that has it, so each list below reads through `data?.`.
  const sati = useMemo(() => {
    if (!data) return [];
    const po = new Map(data.byHour.map((h) => [h.hour, h.measurements]));
    return Array.from({ length: 24 }, (_, h) => ({ sat: h, merenja: po.get(h) ?? 0 }));
  }, [data]);

  // Naruceno a nikad izmereno, po disciplini i po godini. Zbir ovo krije: hemija promasi dvadesetinu,
  // mikrobiologija sve od 2025.
  const poDisciplini = useMemo(() => {
    const m = new Map<string, { ordered: number; unmeasured: number }>();
    for (const x of data?.unmeasured ?? []) {
      const r = m.get(x.discipline) ?? { ordered: 0, unmeasured: 0 };
      r.ordered += x.ordered;
      r.unmeasured += x.unmeasured;
      m.set(x.discipline, r);
    }
    return [...m.entries()]
      .map(([discipline, r]) => ({ discipline, ...r, pct: r.ordered ? (100 * r.unmeasured) / r.ordered : 0 }))
      .sort((a, b) => b.pct - a.pct);
  }, [data]);

  const mikroPoGodini = useMemo(() => {
    const g = new Map<number, { ordered: number; unmeasured: number }>();
    for (const x of data?.unmeasured ?? []) {
      if (x.discipline !== "MICROBIOLOGY") continue;
      const r = g.get(x.year) ?? { ordered: 0, unmeasured: 0 };
      r.ordered += x.ordered;
      r.unmeasured += x.unmeasured;
      g.set(x.year, r);
    }
    return [...g.entries()]
      .map(([year, r]) => ({ year, ...r, pct: r.ordered ? (100 * r.unmeasured) / r.ordered : 0 }))
      .sort((a, b) => a.year - b.year);
  }, [data]);

  if (!data) return <UvidiCekanje label={t("common.loading")} />;

  const satVrh = [...data.byHour].sort((a, b) => b.measurements - a.measurements).slice(0, 2);
  const satUkupno = data.byHour.reduce((s, h) => s + h.measurements, 0);
  const prviUzorkivac = data.samplers.find((s) => s.name);
  const heksan = data.impossible.find((x) => x.key === "HEXANE");
  const bezHeksana = data.impossible
    .filter((x) => x.key !== "HEXANE")
    .reduce((s, x) => s + x.overHundred, 0);
  const negativnih = data.impossible.reduce((s, x) => s + x.negative, 0);
  const granice = data.limits;
  const prosek = data.labelKinds.find((k) => k.kind === "AVERAGE");
  const neispravnih = granice.impossibleRange + granice.underReview;
  const ponovljena = data.repeated[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Sekcija
        naslov={t("insights.years.title", "Deset godina merenja")}
        zapazanje={t(
          "insights.years.note",
          "Obim raste do 2020. i od tada stoji na oko sedam i po hiljada analiza godišnje. Razmak između stubića i linije je broj testova po analizi, i on se kroz godine povećavao.",
        )}
      >
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.perYear} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
            <YAxis
              yAxisId="a"
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <YAxis yAxisId="m" orientation="right" hide />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              labelStyle={{ color: palette.inkStrong }}
              formatter={(value, name) => [n(Number(value)), String(name)]}
            />
            <Bar
              yAxisId="a"
              dataKey="analyses"
              name={t("insights.years.analyses", "Analiza")}
              fill={palette.chart[0] || palette.brandInk}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="m"
              type="monotone"
              dataKey="measurements"
              name={t("insights.years.measurements", "Merenja")}
              stroke={palette.ok}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Sekcija>

      {poDisciplini.length > 0 && (
        <Sekcija
          naslov={t("insights.unmeasured.title", "Naručeno, a nikad izmereno")}
          zapazanje={t(
            "insights.unmeasured.note",
            "Zbir od {{uk}} krije ono što podela pokazuje. Hemija promaši {{hem}}% naručenog. Mikrobiologija promaši {{mik}}%, i niz raste do 100% u 2025. i opet u 2026 — dve godine zaredom nijedan naručen mikrobiološki test nema rezultat u ovom sistemu. Odgovor kvaliteta to objašnjava: mikrobiologija se radi svakodnevno u spoljnoj laboratoriji, nalaz stiže kao PDF i „trenutno se nigde ne unosi”. Rupa nije neurađen posao nego posao čijem rezultatu nema gde da sleti.",
            {
              uk: n(data.unmeasured.reduce((s, x) => s + x.unmeasured, 0)),
              hem: (poDisciplini.find((d) => d.discipline === "CHEMISTRY")?.pct ?? 0).toFixed(1),
              mik: (poDisciplini.find((d) => d.discipline === "MICROBIOLOGY")?.pct ?? 0).toFixed(1),
            },
          )}
        >
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            {poDisciplini.map((d) => (
              <KPI
                key={d.discipline}
                label={disciplinaNaziv(t, d.discipline)}
                value={`${d.pct.toFixed(1)}%`}
                delta={undefined}
                trend={undefined}
                icon={undefined}
              />
            ))}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={mikroPoGodini} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="n"
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <YAxis yAxisId="p" orientation="right" domain={[0, 100]} hide />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                wrapperStyle={{ fontSize: 12, color: "var(--ink-3)" }}
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                formatter={(value, name) =>
                  String(name).includes("%")
                    ? [`${Number(value).toFixed(1)}%`, String(name)]
                    : [n(Number(value)), String(name)]
                }
              />
              <Bar
                yAxisId="n"
                dataKey="ordered"
                name={t("insights.unmeasured.ordered", "Naručeno")}
                fill={palette.chart[0] || palette.brandInk}
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="p"
                type="monotone"
                dataKey="pct"
                name={t("insights.unmeasured.pct", "Nikad izmereno %")}
                stroke={palette.chart[3] || "var(--err)"}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Sekcija>
      )}

      <Sekcija
        naslov={t("insights.hour.title", "Sat uzorkovanja, izvučen iz oznake uzorka")}
        zapazanje={t(
          "insights.hour.note",
          "U celom arhivu nema nijedne kolone sa vremenom. Čas ipak postoji — upisan je u oznaku uzorka, kao „19h” ili „07H”, {{n}} puta. Dva vrha, u {{a}} i {{b}} časova, poklapaju se sa smenom od dvanaest sati. To je podatak koji niko nije nameravao da ostavi.",
          {
            n: n(satUkupno),
            a: satVrh[0] ? String(satVrh[0].hour).padStart(2, "0") : "—",
            b: satVrh[1] ? String(satVrh[1].hour).padStart(2, "0") : "—",
          },
        )}
      >
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={sati} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="sat"
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${String(v).padStart(2, "0")}h`}
            />
            <YAxis tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} width={52} />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              labelFormatter={(label) => `${String(label).padStart(2, "0")}h`}
              formatter={(value) => [n(Number(value)), t("insights.years.measurements", "Merenja")]}
            />
            <Bar dataKey="merenja" radius={[4, 4, 0, 0]}>
              {sati.map((s) => (
                <Cell
                  key={s.sat}
                  fill={satVrh.some((v) => v.hour === s.sat) ? palette.ok : palette.chart[0] || palette.brandInk}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {t("insights.label.breakdown", "Šta još stoji u istom polju, od {{n}} popunjenih oznaka", {
              n: n(data.labelsFilled),
            })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {data.labelKinds.map((k) => (
              <span key={k.kind} className={k.kind === "AVERAGE" ? "tag warn" : "tag neutral"}>
                {vrstaOznake(t, k.kind)}
                <span className="muted" style={{ marginLeft: 6 }}>
                  {n(k.measurements)}
                </span>
              </span>
            ))}
          </div>
          {prosek && prosek.measurements > 0 && (
            <p className="muted" style={{ margin: "10px 0 0", maxWidth: "76ch", lineHeight: 1.55 }}>
              {t(
                "insights.label.averageNote",
                "Jedno od njih nije oznaka nego upozorenje: {{n}} rezultata nosi „prosek”, što znači da upisana vrednost nije jedno merenje nego izračunata sredina. Pomešani sa pojedinačnim merenjima, ti redovi sužavaju rasipanje i krive svaki kvartil u tabeli iznad.",
                { n: n(prosek.measurements) },
              )}
            </p>
          )}
        </div>
      </Sekcija>

      <Sekcija
        naslov={t("insights.limits.title", "Granice iz starog programa ne daju postotak usaglašenosti")}
        zapazanje={t(
          "insights.limits.note",
          "Zato na ovom ekranu nema postotka usaglašenosti. Granica se u starom programu vezivala za artikal i komitenta umesto za specifikaciju proizvoda.",
        )}
      >
        <div className="kpi-grid" style={{ marginBottom: 0 }}>
          <KPI
            label={t("insights.limits.impossible", "Donja vrednost iznad gornje")}
            value={n(granice.impossibleRange)}
            delta={undefined}
            trend={undefined}
            icon={undefined}
          />
          <KPI
            label={t("insights.limits.conflicting", "Artikala sa granicama koje se ne slažu")}
            value={n(granice.conflictingPairs)}
            delta={undefined}
            trend={undefined}
            icon={undefined}
          />
          <KPI
            label={t("insights.limits.zero", "Donja vrednost upisana kao nula")}
            value={n(granice.zeroLowerBound)}
            delta={undefined}
            trend={undefined}
            icon={undefined}
          />
          <KPI
            label={t("insights.limits.withoutLimit", "Artikala bez ijedne granice")}
            value={n(granice.articlesWithoutLimit)}
            delta={undefined}
            trend={undefined}
            icon={undefined}
          />
        </div>
        <p className="muted" style={{ margin: "12px 0 0" }}>
          {t(
            "insights.limits.lead",
            "Od {{ukupno}} nasleđenih granica, {{neispravnih}} ne može da radi kao kapija. Mereno je {{mereno}} artikala, a {{bez}} od njih nema nijednu granicu po kojoj bi se rezultat ocenio.",
            {
              ukupno: n(granice.total),
              neispravnih: n(neispravnih),
              mereno: n(granice.articlesMeasured),
              bez: n(granice.articlesWithoutLimit),
            },
          )}
        </p>
      </Sekcija>

      <Sekcija
        naslov={t("insights.parameters.title", "Šta se meri i kako su vrednosti raspoređene")}
        zapazanje={t(
          "insights.parameters.note",
          "Prve tri stavke nose polovinu svih merenja. Raspon od najnižeg do najvišeg pokazuje i gde se krije greška u unosu: kad je najviša vrednost višestruko iznad gornjeg kvartila, to nije proces nego zarez.",
        )}
      >
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("insights.parameters.parameter", "Parametar")}</th>
              <th style={{ textAlign: "right" }}>{t("insights.years.measurements", "Merenja")}</th>
              <th style={{ textAlign: "right" }}>{t("lab.param.min", "Najniže")}</th>
              <th style={{ textAlign: "right" }}>{t("insights.parameters.p25", "25%")}</th>
              <th style={{ textAlign: "right" }}>{t("insights.parameters.median", "Medijana")}</th>
              <th style={{ textAlign: "right" }}>{t("insights.parameters.p75", "75%")}</th>
              <th style={{ textAlign: "right" }}>{t("lab.param.max", "Najviše")}</th>
            </tr>
          </thead>
          <tbody>
            {data.parameters.map((p) => (
              <tr key={p.parameterId}>
                <td>
                  {localizedName({ name: p.key, nameI18n: p.nameI18n }, lang)}
                  <span className="muted" style={{ marginLeft: 6 }}>
                    {p.unit}
                  </span>
                </td>
                <td style={broj}>{n(p.measurements)}</td>
                <td style={broj}>{p.minValue ?? "—"}</td>
                <td style={broj}>{p.p25 ?? "—"}</td>
                <td style={broj}>{p.p50 ?? "—"}</td>
                <td style={broj}>{p.p75 ?? "—"}</td>
                <td style={broj}>{p.maxValue ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sekcija>

      <Sekcija
        naslov={t("insights.samplers.title", "Ko uzima uzorke")}
        zapazanje={
          prviUzorkivac
            ? t(
                "insights.samplers.note",
                "U registru je {{ukupno}} imena, ali jedan čovek potpisuje {{udeo}}% svih uzorkovanja. To nije primedba na njega nego na raspored: kad jedan čovek nosi tri četvrtine uzorkovanja, njegov godišnji odmor je rupa u evidenciji kvaliteta.",
                { ukupno: n(data.samplersTotal), udeo: (prviUzorkivac.share * 100).toFixed(1) },
              )
            : ""
        }
      >
        <ResponsiveContainer width="100%" height={Math.max(180, data.samplers.length * 34)}>
          <BarChart
            data={data.samplers.map((s) => ({
              ime: s.name ?? t("insights.samplers.others", "ostali"),
              analiza: s.analyses,
            }))}
            layout="vertical"
            margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
          >
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="ime"
              width={170}
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              formatter={(value) => [n(Number(value)), t("lab.kpi.analyses", "Analiza")]}
            />
            <Bar dataKey="analiza" fill={palette.chart[1] || palette.brand} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Sekcija>

      <Sekcija
        naslov={t("insights.shift.title", "Oznaka smene ne beleži smenu")}
        zapazanje={t(
          "insights.shift.note",
          "Da je ovo rotacija od četiri smene, B, C i D bili bi istog reda veličine kao A. Prave rotacione oznake (A-1 do D-2) nose {{rot}} zapisa, i to samo na četiri kontrolne tačke i kod šest ljudi. Isti uzorkivač istog dana na istoj tački upisuje i „A” i rotaciju, a jedan čovek ne radi u dve smene. Posledica: nijedan pokazatelj iz arhiva ne sme da se deli po smeni — ko to zatraži, dobiće izmišljen broj.",
          { rot: n(data.shiftRotation) },
        )}
      >
        <ResponsiveContainer width="100%" height={Math.max(180, data.shifts.length * 26)}>
          <BarChart
            data={data.shifts.map((s) => ({
              oznaka: s.code ?? t("insights.shift.empty", "(prazno)"),
              analiza: s.analyses,
            }))}
            layout="vertical"
            margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
          >
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="oznaka"
              width={90}
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              formatter={(value) => [n(Number(value)), t("lab.kpi.analyses", "Analiza")]}
            />
            <Bar dataKey="analiza" radius={[0, 4, 4, 0]}>
              {data.shifts.map((s) => (
                <Cell
                  key={s.code ?? "prazno"}
                  fill={s.code && s.code.includes("-") ? palette.ok : palette.chart[2] || palette.inkMuted}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Sekcija>

      {(heksan || bezHeksana > 0 || negativnih > 0) && (
        <Sekcija
          naslov={t("insights.impossible.title", "Vrednosti koje ne mogu biti to što kolona tvrdi")}
          zapazanje={t(
            "insights.impossible.note",
            "Kad procenat pređe sto, to je zarez koji je ispao pri unosu.",
          )}
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("insights.parameters.parameter", "Parametar")}</th>
                <th>{t("insights.impossible.unit", "Jedinica")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.impossible.over", "Preko 100")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.impossible.negative", "Negativnih")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.years.measurements", "Merenja")}</th>
              </tr>
            </thead>
            <tbody>
              {data.impossible.map((x) => (
                <tr key={x.key}>
                  <td>{x.key}</td>
                  <td className="muted">{x.unit}</td>
                  <td style={broj}>{x.overHundred > 0 ? n(x.overHundred) : "—"}</td>
                  <td style={broj}>{x.negative > 0 ? n(x.negative) : "—"}</td>
                  <td style={broj}>{n(x.measurements)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sekcija>
      )}

      {ponovljena && (
        <Sekcija
          naslov={t("insights.repeated.title", "Vrednost koja se ponavlja previše da bi bila merenje")}
          zapazanje={t(
            "insights.repeated.note",
            "Nije dovoljno da se vrednost ponavlja: heksan čita tačno nulu 3.419 puta u otpadnoj vodi, a nula je ono što provera ispuštanja i traži. Ovde su samo one koje štrče i kojima je okolina prazna. {{p}} čita {{v}} čak {{n}} puta, a najbliže vrednosti ispod i iznad zajedno {{s}} puta — to više nije merena veličina.",
            {
              v: String(ponovljena.value),
              n: n(ponovljena.occurrences),
              p: ponovljena.key,
              s: n(ponovljena.neighbours),
            },
          )}
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("insights.parameters.parameter", "Parametar")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.repeated.value", "Vrednost")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.repeated.times", "Puta")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.repeated.neighbours", "Susedne vrednosti")}</th>
                <th style={{ textAlign: "right" }}>{t("insights.repeated.share", "Udeo")}</th>
              </tr>
            </thead>
            <tbody>
              {data.repeated.map((x) => (
                <tr key={`${x.key}-${x.value}`}>
                  <td>{x.key}</td>
                  <td style={broj}>{String(x.value)}</td>
                  <td style={broj}>{n(x.occurrences)}</td>
                  <td style={broj}>{n(x.neighbours)}</td>
                  <td style={broj}>
                    {x.parameterTotal > 0 ? `${((100 * x.occurrences) / x.parameterTotal).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sekcija>
      )}
    </div>
  );
}

/**
 * The wait, in the shape this screen has: a row of figures, then one card per chart.
 *
 * A line of text says that something is happening but not what is coming, and the page jumps when it
 * arrives. These blocks stand where the figures and the bars will stand, so nothing moves. Only the
 * shapes of the system's skeleton are used, so the colours follow the theme without a token of its own.
 */
function UvidiCekanje({ label }: { label: string }) {
  // Bar heights vary so the block reads as a chart and not as one grey slab.
  const visineStubica = [52, 74, 38, 86, 61, 92, 45, 70, 55, 80, 34, 66];
  // The sections in the order they arrive: the years, then the one that carries the row of figures,
  // then the hour of sampling. Heights are the ones the charts themselves are given.
  const sekcije = [
    { visina: 260, brojevi: false },
    { visina: 220, brojevi: true },
    { visina: 220, brojevi: false },
  ];
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{label}</span>
      {sekcije.map((s, i) => (
        <div className="card" key={i}>
          <div className="card-h">
            <span className="sk-block" style={{ width: "38%", height: 12 }} aria-hidden="true" />
          </div>
          <div className="card-b">
            {s.brojevi && (
              <div className="kpi-grid" style={{ marginBottom: 16 }}>
                {[0, 1, 2, 3].map((k) => (
                  <div className="kpi" key={k}>
                    <span className="sk-block" style={{ width: "62%", height: 10 }} aria-hidden="true" />
                    <span className="sk-block" style={{ width: "45%", height: 24 }} aria-hidden="true" />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: s.visina }}>
              {visineStubica.map((v, b) => (
                <span
                  key={b}
                  className="sk-block"
                  style={{ flex: 1, height: `${v}%` }}
                  aria-hidden="true"
                />
              ))}
            </div>
            <span
              className="sk-block"
              style={{ width: "84%", height: 10, marginTop: 16 }}
              aria-hidden="true"
            />
            <span
              className="sk-block"
              style={{ width: "62%", height: 10, marginTop: 6 }}
              aria-hidden="true"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Jedna sekcija: naslov, grafikon ili tabela, pa rečenica koja kaže šta se na njoj vidi. */
function Sekcija({
  naslov,
  zapazanje,
  children,
}: {
  naslov: string;
  zapazanje: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-h">
        <strong>{naslov}</strong>
      </div>
      <div className="card-b">
        {children}
        {zapazanje && (
          <p className="muted" style={{ margin: "14px 0 0", maxWidth: "76ch", lineHeight: 1.55 }}>
            {zapazanje}
          </p>
        )}
      </div>
    </div>
  );
}

/** Imena disciplina. Ista tri ključa koja nosi parametar. */
function disciplinaNaziv(t: (k: string, d: string) => string, d: string): string {
  if (d === "MICROBIOLOGY") return t("lab.discipline.MICROBIOLOGY", "Mikrobiologija");
  if (d === "GRAIN") return t("lab.discipline.GRAIN", "Sojino zrno");
  return t("lab.discipline.CHEMISTRY", "Hemija");
}

/** Imena vrsta oznake uzorka. Ključevi su mašinski; ovo je ono što čovek čita. */
function vrstaOznake(t: (k: string, d: string) => string, kind: string): string {
  switch (kind) {
    case "HOUR":
      return t("insights.label.hour", "čas");
    case "AVERAGE":
      return t("insights.label.average", "prosek, ne merenje");
    case "ROMAN":
      return t("insights.label.roman", "rimski broj");
    case "NUMBER":
      return t("insights.label.number", "goli broj");
    default:
      return t("insights.label.other", "ostalo");
  }
}

const broj = { textAlign: "right", fontVariantNumeric: "tabular-nums" } as const;
